<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import router from '@/router';
import { useRoute } from 'vue-router';

const route = useRoute();

const props = defineProps({
  mainColor: {
    type: String,
    required: true,
  },
  backgroundColor: {
    type: String,
    required: true,
  },
  headerText: {
    type: String,
    required: true,
  },
});

const windowWidth = ref(0);
const isMobile = ref(false);
const mobileWidth = ref(780);

const arrowColor = computed(() => {
  return `fill:${props.mainColor};`;
});

function checkScreen() {
  windowWidth.value = window.innerWidth;
  if (windowWidth.value <= mobileWidth.value) {
    isMobile.value = true;
    return;
  }
  isMobile.value = false;
  return;
}

function goBack() {
  if (window.history.length > 1 && window.history.state.position !== 0) {
    router.go(-1);
  } else {
    const params = route.params;
    router.push({ name: 'ClientId', params }).then(() => {
      router.go(0);
    });
  }
}

onMounted(() => {
  window.addEventListener('resize', checkScreen);
  checkScreen();
});

onUnmounted(() => {
  window.removeEventListener('resize', checkScreen);
});
</script>

<template>
  <div class="header">
    <nav>
      <button v-if="!isMobile" class="button back" @click="goBack">
        {{ $t('appCore.apps.event.common.back') }}
      </button>
      <button v-else class="back-mobile" @click="goBack">
        <svg
          id="Capa_1"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          width="30px"
          height="30px"
          viewBox="0 0 156.199 156.199"
          xml:space="preserve"
        >
          <g>
            <path
              :style="arrowColor"
              d="M102.496,45.204h-2.499v-0.012l-77.613-0.368l32.958-32.959l-8.277-8.28L0,50.65l47.882,47.889
        l8.28-8.28l-33.719-33.73l71.642,0.346v0.04h8.417c23.151,0,41.993,18.838,41.993,41.997c0,23.151-18.842,41.992-41.993,41.992H0
        v11.711h102.496c29.613,0,53.703-24.09,53.703-53.703C156.199,69.293,132.109,45.204,102.496,45.204z"
            />
          </g>
        </svg>
      </button>
      <h1 v-if="headerText" class="core-default-h2 title">
        {{ headerText }}
      </h1>
      <slot></slot>
    </nav>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styleConstans';

.header {
  position: fixed;
  display: flex;
  width: 100%;
  padding-top: 20px;
  padding-bottom: 20px;
  z-index: 10;
  background-color: v-bind(backgroundColor);
  font-size: 18px;

  nav {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 90%;
    margin: 0 auto;
    gap: 10px;
    min-height: 71px;

    @media screen and (max-width: $media-tablet) {
      min-height: 30px;
    }

    @media screen and (max-width: $media-phone) {
      justify-content: space-evenly;
    }

    .title {
      flex: 1;
      margin: 0 180px;
      text-align: center;
      color: v-bind(mainColor);
      font-size: 60px;
      line-height: 66px;

      @media screen and (max-width: $media-tablet) {
        font-size: 2.5em;
        margin: 0 45px;
      }

      @media screen and (max-width: $media-phone) {
        font-size: 1.7em;
        line-height: 50px;
        flex: 0;
      }
    }

    .back {
      position: absolute;
      left: 0;
      display: flex;
      align-items: center;
      flex: 0;
    }

    .back-mobile {
      position: absolute;
      left: 0;
      border: 0;
    }
  }
}

.button {
  border-radius: 48.707px;
  border: 2px solid v-bind(mainColor);
  color: v-bind(mainColor);
  padding: 15px 25px;
  font-size: 25px;
  white-space: nowrap;
  text-align: center;
  text-transform: uppercase;
  transition: background-color 1s ease;

  &:hover {
    background-color: v-bind(mainColor);
    color: v-bind(backgroundColor);
    cursor: pointer;
  }

  @media screen and (max-width: $media-phone) {
    font-size: 18px;
    line-height: 30px;
    padding: 10px 20px;
  }
}
</style>
